import { createAction, createReducer, Dispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { valueOf } from 'shared';
import { ErrorContent, MaintenanceErrorContent } from '../../fetcher';
import { updatePage } from '../commons';
import { RootState } from '../store';

export const CURRENT_PAGE_STATE_TYPE = {
  loading: 'loading',
  authentication: 'authentication',
  input: 'input',
  confirm: 'confirm',
  complete: 'complete',
  receptionAuthenticationError: 'receptionAuthenticationError',
  private: 'private',
  maintenance: 'maintenance',
  error: 'error',
} as const;

type CurrentPageStateMaintenance = {
  type: typeof CURRENT_PAGE_STATE_TYPE.maintenance;
  error: MaintenanceErrorContent;
};

type CurrentPageStateError = {
  type: typeof CURRENT_PAGE_STATE_TYPE.error;
  error: ErrorContent;
};

export type CurrentPageState =
  | {
      type: valueOf<
        Omit<typeof CURRENT_PAGE_STATE_TYPE, 'maintenance' | 'error'>
      >;
    }
  | CurrentPageStateMaintenance
  | CurrentPageStateError;

export const currentPageStateInitialState: CurrentPageState = {
  type: CURRENT_PAGE_STATE_TYPE.loading,
};

// action
const SET_CURRENT_PAGE_STATE = 'currentPageState/setCurrentPageState';
export const setCurrentPageState = createAction<
  CurrentPageState,
  typeof SET_CURRENT_PAGE_STATE
>(SET_CURRENT_PAGE_STATE);
export type SetCurrentPageStateAction = ReturnType<typeof setCurrentPageState>;

// dispatch
export const useSetCurrentPageStateDispatch =
  (): Dispatch<SetCurrentPageStateAction> =>
    useDispatch<Dispatch<SetCurrentPageStateAction>>();

// reducer
export const currentPageStateReducer = createReducer<CurrentPageState>(
  currentPageStateInitialState,
  (builder) => {
    builder
      .addCase(setCurrentPageState, (_, action) => {
        return action.payload;
      })
      .addCase(updatePage, (oldState, action) => {
        const { nextPageState } = action.payload;
        if (nextPageState && nextPageState.type !== oldState.type) {
          return nextPageState;
        }
      });
  },
);

// selector
export const useCurrentPageState = (): CurrentPageState =>
  useSelector<RootState, CurrentPageState>(
    (rootState) => rootState.currentPageState,
  );
